import type {
  CoordinateFormat,
  ParsedCoordinate,
  DDCoordinate,
  DMSCoordinate,
  DMSPart,
  UTMCoordinate,
} from '../../types/geo'

// ── DD ──────────────────────────────────────────────────────────────────────

export function ddToDms(decimal: number, axis: 'lat' | 'lon'): DMSPart {
  const abs = Math.abs(decimal)
  const degrees = Math.floor(abs)
  const minutesFloat = (abs - degrees) * 60
  const minutes = Math.floor(minutesFloat)
  const seconds = parseFloat(((minutesFloat - minutes) * 60).toFixed(4))
  const hemisphere =
    axis === 'lat' ? (decimal >= 0 ? 'N' : 'S') : decimal >= 0 ? 'E' : 'W'
  return { degrees, minutes, seconds, hemisphere }
}

export function formatDDPair(lat: number, lon: number): string {
  return `${lat.toFixed(6)}, ${lon.toFixed(6)}`
}

export function formatDMS(part: DMSPart): string {
  return `${part.degrees}° ${part.minutes}' ${part.seconds.toFixed(2)}" ${part.hemisphere}`
}

export function formatDMSPair(dms: DMSCoordinate): string {
  return `${formatDMS(dms.lat)}, ${formatDMS(dms.lon)}`
}

// ── DMS parsing ──────────────────────────────────────────────────────────────

const DMS_PATTERN =
  /^([+-]?\d{1,3})[°\s]?\s*(\d{1,2})['′\s]?\s*([\d.]+)[\"″\s]?\s*([NSns]?)\s*[,/\s]+\s*([+-]?\d{1,3})[°\s]?\s*(\d{1,2})['′\s]?\s*([\d.]+)[\"″\s]?\s*([EWew]?)$/

export function parseDms(input: string): DDCoordinate | null {
  const m = input.trim().match(DMS_PATTERN)
  if (!m) return null
  const [, d1, m1, s1, h1, d2, m2, s2, h2] = m
  const latSign = h1.toUpperCase() === 'S' ? -1 : 1
  const lonSign = h2.toUpperCase() === 'W' ? -1 : 1
  const lat = latSign * (parseInt(d1) + parseInt(m1) / 60 + parseFloat(s1) / 3600)
  const lon = lonSign * (parseInt(d2) + parseInt(m2) / 60 + parseFloat(s2) / 3600)
  if (!isValidDD({ format: 'dd', lat, lon })) return null
  return { format: 'dd', lat, lon }
}

function isValidDD(c: DDCoordinate): boolean {
  return c.lat >= -90 && c.lat <= 90 && c.lon >= -180 && c.lon <= 180
}

// ── UTM parsing ──────────────────────────────────────────────────────────────

const UTM_PATTERN =
  /^(\d{1,2})\s*([NSns])\s+([\d.]+)[Ee]?\s+([\d.]+)[Nn]?$/

export function parseUtm(input: string): UTMCoordinate | null {
  const m = input.trim().match(UTM_PATTERN)
  if (!m) return null
  return {
    format: 'utm',
    zone: parseInt(m[1]),
    hemisphere: m[2].toUpperCase() as 'N' | 'S',
    easting: parseFloat(m[3]),
    northing: parseFloat(m[4]),
  }
}

// ── Format detection ──────────────────────────────────────────────────────────

const DD_PATTERN =
  /^([+-]?\d{1,2}(?:\.\d+)?)\s*[,\s]\s*([+-]?\d{1,3}(?:\.\d+)?)$/

const MGRS_PATTERN = /^[0-9]{1,2}[C-HJ-NP-X][A-HJ-NP-Z]{2}[0-9]{0,10}$/i

export function detectCoordinateFormat(input: string): CoordinateFormat | null {
  const cleaned = input.trim()
  if (!cleaned) return null

  // DD: "38.5, -77.1"
  if (DD_PATTERN.test(cleaned)) return 'dd'

  // DMS check
  if (DMS_PATTERN.test(cleaned)) return 'dms'

  // MGRS (spaces optional)
  const noSpaces = cleaned.replace(/\s+/g, '').toUpperCase()
  if (MGRS_PATTERN.test(noSpaces)) return 'mgrs'

  // UTM
  if (UTM_PATTERN.test(cleaned)) return 'utm'

  return null
}

export function parseCoordinate(input: string): ParsedCoordinate | null {
  const cleaned = input.trim()
  const fmt = detectCoordinateFormat(cleaned)
  if (!fmt) return null

  if (fmt === 'dd') {
    const m = cleaned.match(DD_PATTERN)!
    const lat = parseFloat(m[1])
    const lon = parseFloat(m[2])
    return { format: 'dd', lat, lon }
  }

  if (fmt === 'dms') return parseDms(cleaned)

  if (fmt === 'mgrs') {
    return { format: 'mgrs', mgrs: cleaned, precision: 5 }
  }

  if (fmt === 'utm') return parseUtm(cleaned)

  return null
}

export function formatCoordinate(
  lat: number,
  lon: number,
  fmt: CoordinateFormat,
  mgrsStr?: string
): string {
  if (fmt === 'dd') return formatDDPair(lat, lon)
  if (fmt === 'dms') {
    const dms: DMSCoordinate = {
      format: 'dms',
      lat: ddToDms(lat, 'lat'),
      lon: ddToDms(lon, 'lon'),
    }
    return formatDMSPair(dms)
  }
  if (fmt === 'mgrs') return mgrsStr ?? ''
  return formatDDPair(lat, lon)
}

/**
 * Detect which field IDs in a template JSON might contain geo data
 * based on their type string.
 */
export function detectGeoFields(fields: Array<{ id: string; type: string }>): string[] {
  const geoTypes = [
    'coordinate_mgrs',
    'coordinate_any',
    'coordinate_dd',
    'coordinate_dms',
    'geojson_polygon',
    'geojson_point',
    'geojson_linestring',
    'geojson_geometry',
    'bbox',
    'mgrs_list',
  ]
  return fields.filter((f) => geoTypes.includes(f.type)).map((f) => f.id)
}
